import { css, PropertyValues, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { waitFor } from '../modules/async';
import { KryptonConstants } from '../modules/krypton_constants';
import { ICONS } from '../modules/resources';
import { lookupModule } from '../types/mapping';
import { KryptonEventDetail } from './krypton-event-detail';
import { MenuItem, MenuItemJsonData } from './menu-item';

const STANDARD_MENU = {
    "Workspace": true,
    "Filter": true,
    "Export": true,
    "Print": true,
    "Refresh": true,
    "More": true
};

export class MenuContextJsonData {
    menuId: string;
    actionOnClick: string;
    description: string;
    actiontarget: ContextMenuTarget;
    label: string;
    menuGroup: string;
    targetWindow: ContextMenuTargetWindow;
}

export class WorkspaceJsonData {
    widgetId: number;
    actionId: number;
    rightId: number;
    displayOrder: number;
    widgetName: string;
    accessRight: string;
    userAction: string;
    detailUserAction: string;
    applicationUrl: string;
}

export class ContextMenu {

    menu: Object = Object.assign({}, STANDARD_MENU);

    constructor() {

        const workspace = new MenuContext(); //TODO this doesn't get reloaded with each module
        workspace.label = "Workspace";
        workspace.setAttribute("slot", "context-menu");
        this.menu["Workspace"] = workspace;

        const more = new MenuContext();
        more.label = "More";
        more.setAttribute("slot", "context-menu");
        this.menu["More"] = more;

    }

    add(menuContext: MenuContext) {
        if (this.isOverflow(menuContext)) {
            this.menu["More"].appendChild(menuContext);
        } else {
            menuContext.setAttribute("slot", "context-menu");
            this.menu[menuContext.label] = menuContext;
        }
    }

    addWorkspaceMenu(menuItem: MenuItem) {
        if (this.isOverflow(menuItem)) {
            this.menu["Workspace"].appendChild(menuItem);
        } else {
            menuItem.setAttribute("slot", "context-menu");
            this.menu[menuItem.label] = menuItem;
        }
    }

    isOverflow(menuItem: MenuItem) {
        return !this.menu.hasOwnProperty(menuItem.label);
    }

    *iterator(): IterableIterator<MenuContext> {
        for (let key in this.menu) {
            let cm = this.menu[key];
            if (!(cm instanceof MenuContext)) {
                let data = new MenuContextJsonData();
                data.label = key;
                cm = new MenuContext(data); //-- placeholder item
                cm.setAttribute("slot", "context-menu");
            }
            yield cm;
        }
    }

    [Symbol.iterator]() {
        return this.iterator();
    }

    size() {
        return Object.keys(this.menu).length;
    }

}

//TODO find all values of these from the database
type ContextMenuTarget = "popup" | "unknown";
type ContextMenuTargetWindow = "N" | "J" | "R" | "unknown";


export class MenuContext extends MenuItem {

    public static animationTime = 250;

    static styles = css`
        :host {
            box-sizing: border-box;
            cursor: pointer;
            text-align: center;
            display: flex;
            align-items: center;
            flex-direction: column;
            height: 100%;
            margin: 0.1em 0.2em;
            position: relative;
            min-width: 50px;
        }

        :host(.disabled) {
            opacity: 0.35;
            pointer-events: none;
            cursor: default;
        }

        label {
            font-size: 0.8em;
            font-weight: normal;
            margin: 0;
            padding: 0;
            cursor: pointer;
            color: black;
        }

        label::before {
            content: '';
            display: flex;
            justify-content: center;
            min-height: 20px;
            background-image: var(--bg-image-url, url('${unsafeCSS(ICONS.circle.file)}'));
            background-position: center center;
            background-repeat: no-repeat;
        }

        #options {
            display: none;
            position: absolute;
            background: #ffffff;
            border: 3px solid #cecdcb;
            right: 0.5em;
            top: 116%;
            opacity: 0;
            min-width: 20em;
            -webkit-transition: opacity 350ms ease;
            -moz-transition: opacity 350ms ease;
            -o-transition: opacity 350ms ease;
            transition: opacity 350ms ease;
        }

        #options:after,
        #options:before {
            bottom: 100%;
            right: 4px;
            border: solid transparent;
            content: " ";
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
        }

        #options:after {
            border-color: rgba(255, 255, 255, 0);
            border-bottom-color: #ffffff;
            border-width: 9px;
            margin-left: -9px;
        }

        #options:before {
            border-color: rgba(206, 205, 203, 0);
            border-bottom-color: #cecdcb;
            border-width: 13px;
            margin-left: -13px;
            right: 0px;
        }

        #options.wake {
            display: block;
        }

        :host(.category.open) #options.wake {
            opacity: 1;
            z-index: 9700;
        }

        :host(.category.pin) #options  {
            display: block;
            opacity: 1;
        }

        :host(.option) {
            border-bottom: 2px #eee solid;
            flex-direction: row;
            padding: 0.7rem 0.25rem;
            margin: 0;
            font-size: 1em;
            z-index: 9700;
            background-color: white;
        }

        :host(.option:last-of-type) {
            border: none;
        }

        :host(.option:hover) {
            background-color: #eee;
        }

        :host(.option) #label {
            display: flex;
            flex-direction: row;
            font-size: 1em;
            color: #000;
        }

        :host(.option) #label::before {
            width: 35px;
        }
        .workspace-options {
            max-height: 70vh;
            overflow-y: auto;
        }
        @media only screen and (max-width: 543px){
            .workspace-options {
                right: auto;
                left: 20px;
            }
            .workspace-options::after, .workspace-options::before {
                bottom: 100%;
                left: 20px;
                right: auto;
                border: solid transparent;
                content: " ";
                height: 0px;
                width: 0px;
                position: absolute;
                pointer-events: none;
            }
        }
    `;

    constructor(data?: MenuContextJsonData) {
        super();
        if (!data) return;
        this.data = data;
        this.label = data.label;
        this.title = data.description;
        this.action = data.actionOnClick;
        this.target = data.actiontarget;
        this.targetWindow = data.targetWindow;
    }

    @property({ type: String }) accessor target: ContextMenuTarget;
    @property({ type: String }) accessor targetWindow: ContextMenuTargetWindow;

    connectedCallback() {
        this.addEventListener("mouseenter", this.onFocus.bind(this)); //TODO these events are finicky with parent/child events, in the old framework we used a flag but it required a lot of overhead code
        this.addEventListener("mouseleave", this.onBlur.bind(this));
        super.connectedCallback();
    }

    firstUpdated(changedPropertes: PropertyValues) {
        if (!this.options.length && !this.action) {
            this.disabled = true;
        }
        super.firstUpdated(changedPropertes);
        this.setIcon();
    }

    async expandOptions() {
        if (!this.category)
            return;
        this.fire(KryptonEventDetail.MENU_ITEM_EVENT, new KryptonEventDetail(KryptonEventDetail.EXPAND, KryptonEventDetail.MENU_ITEM_EVENT, { open: true }));
        this.classList.add("animating");
        const options = this.shadowRoot?.getElementById("options") as HTMLElement;
        options.classList.add("wake");
        await waitFor(0);
        this.classList.add("open");
        this.classList.remove("animating");
        await waitFor(KryptonConstants.showHideAnimationTime);
    }

    async collapseOptions() {
        if (!this.category)
            return;
        this.classList.add("animating");
        const options = this.shadowRoot?.getElementById("options") as HTMLElement;
        this.classList.remove("open");
        this.fire(KryptonEventDetail.MENU_ITEM_EVENT, new KryptonEventDetail(KryptonEventDetail.COLLAPSE, KryptonEventDetail.MENU_ITEM_EVENT, { open: false }));
        await waitFor(KryptonConstants.showHideIntermediateAnimationTime);
        options.classList.remove("wake");
        this.classList.remove("animating");
        this.querySelectorAll(".category.open").forEach(m => (m as MenuContext).collapseOptions())
        await waitFor(KryptonConstants.showHideAnimationTime);
    }

    _pin: boolean = false;
    _timerHandler: any;
    _lastEventType: String;


    get pin() {
        return this._pin;
    }

    @property({ type: Boolean, reflect: true })
    set pin(pin) {
        const oldValue = this._pin;
        this._pin = pin;
        this.classList[pin ? "add" : "remove"]("pin");
        this.requestUpdate("pin", oldValue);
    }

    active: boolean = false;

    onFocus(e: MouseEvent) {
        e.stopPropagation();
        this.handleEvent("focus", 100);
    }

    onBlur(e: MouseEvent) {
        e.stopPropagation();
        this.handleEvent("blur", 250);
    }

    handleEvent(eventType: String, delay: number) {
        if (!this.category)
            return;
        if (this._timerHandler) {
            clearTimeout(this._timerHandler)
        }
        this._lastEventType = eventType
        this._timerHandler = setTimeout(() => this.openOrCollapseOptions(), delay);
    }

    openOrCollapseOptions() {
        if (this._lastEventType === "focus") {
            this.active = true;
            // onContextMenu should be triggered just before the more options become visible
            // With new implementation, we tie onContextMenu to the page, not to the window any more.  So the page onContextMenu will never be overwritten
            // by any other module's onContextMenu
            let onContextMenu = this.closest("page-element")?.onContextMenu;
            if (onContextMenu) {
                onContextMenu();
            }
            this.expandOptions();
        } else {
            this.active = false;
            !this.active && this.collapseOptions();
        }
    }

    onClick(e: Event): boolean {

        e.stopPropagation();

        if (this.disabled)
            return false;

        if (this.clickProtection.enabled) {
            if (this.clickProtection.active) {
                console.log("click protected!");
                this.clearClickProtection();
                return false;
            }
            this.clickProtection.active = true;
            this.clearClickProtection();
        }

        if (this.category) {
            if (!window["responsive"]?.is?.mobile?.any) {
                this.pin = !this.pin;
            }
            return true;
        } else {
            this.parentElement?.classList?.remove("pin");
            this.doAction(this.targetWindow === "N");
            return true;
        }

    }

    get label() {
        return super.label;
    }

    set label(label) {
        const oldValue = this._label;
        this._label = label;
        this.tag = label;
        this.requestUpdate("label", oldValue);
    }
    setIcon() {
        let normalizedLabel = this.label.replace(/[\W,%]/g, "").toLowerCase()
            , module = lookupModule(document.querySelector("page-element")?.querySelector("module-header")?.module?.toLowerCase())
            , icon;
        if (this.parentElement instanceof MenuContext) {
            icon = "/resources/module/" + module + "/menu/context/" + normalizedLabel + ".svg";
        } else {
            icon = "/resources/module/default/menu/context/" + normalizedLabel + ".svg"
        }
        const labelElement = this.shadowRoot?.getElementById("label");
        labelElement?.style.setProperty("--bg-image-url", "url('" + icon + "')");
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'menu-context': MenuContext;
    }
}

export class WorkspaceMenu extends MenuItem {
    static styles = css`
        :host {
            box-sizing: border-box;
            cursor: pointer;
            text-align: left;
            display: flex;
            align-items: center;
            flex-direction: column;
            height: 100%;
            margin: 0.1em 0.2em;
            position: relative;
            min-width: 50px;
            background-color: white!important;
        }

        :host(.disabled) {
            opacity: 0.35;
            pointer-events: none;
            cursor: default;
        }

        label {
            font-size: 0.8em;
            font-weight: normal;
            margin: 0;
            padding: 0;
            cursor: pointer;
        }

        label::before {
            content: '';
            display: flex;
            justify-content: center;
            min-height: 20px;
        }

        #options {
            display: none;
            position: absolute;
            background: #ffffff;
            border: 3px solid #cecdcb;
            right: 0.5em;
            top: 116%;
            opacity: 0;
            min-width: 20em;
            -webkit-transition: opacity 350ms ease;
            -moz-transition: opacity 350ms ease;
            -o-transition: opacity 350ms ease;
            transition: opacity 350ms ease;
        }

        #options:after,
        #options:before {
            bottom: 100%;
            right: 4px;
            border: solid transparent;
            content: " ";
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
        }

        #options:after {
            border-color: rgba(255, 255, 255, 0);
            border-bottom-color: #ffffff;
            border-width: 9px;
            margin-left: -9px;
        }

        #options:before {
            border-color: rgba(206, 205, 203, 0);
            border-bottom-color: #cecdcb;
            border-width: 13px;
            margin-left: -13px;
            right: 0px;
        }

        #options.wake {
            display: block;
        }

        :host(.category.open) #options.wake {
            opacity: 1;
            z-index: 9700;
        }

        :host(.category.pin) #options  {
            display: block;
            opacity: 1;
        }

        :host(.option) {
            border-bottom: 2px #eee solid;
            flex-direction: row;
            padding: 0.7rem 0.25rem;
            margin: 0;
            font-size: 1em;
            z-index: 9700;
            background-color: white;
        }

        :host(.option:last-of-type) {
            border: none;
        }

        :host(.option:hover) {
            background-color: #eee!important;
        }

        :host(.option) #label {
            display: flex;
            flex-direction: row;
            font-size: 1em;
            color: #004693;
        }

        :host(.option) #label::before {
            width: 35px;
        }

    `;

    constructor(data?: MenuItemJsonData, options?: MenuItemJsonData[]) {
        super(data, options);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'workspace-menu': WorkspaceMenu;
    }
}
